/**
 * 组织上下文状态管理
 *
 * active_org_id 持久化到 sessionStorage（标签页级别隔离，关闭标签页自动清除）。
 * 前端所有 API 请求通过 X-Active-Org-Id 请求头携带当前激活的组织 ID。
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ContextType, MyContexts, OrgItem } from '@/types/user';
import {
  createOrgContextChangedEvent,
  getOrgContextTabId,
  publishOrgContextChangedEvent,
} from '@/lib/org-context-cross-tab';

/** 便于页面从 store 就近引用，与同文件 `ContextType` 一致 */
export type { ContextType };

const SESSION_KEY_ACTIVE_ORG_ID = 'active_org_id';
const SESSION_KEY_ACTIVE_CONTEXT = 'active_context';

// ── 持久化工具 ─────────────────────────────────────────────────

export function getStoredActiveOrgId(): string | null {
  return sessionStorage.getItem(SESSION_KEY_ACTIVE_ORG_ID);
}

/** 个人身份（0）也是已初始化的上下文，不能通过组织 ID 是否为空判断。 */
export function hasStoredActiveContext(): boolean {
  const raw = sessionStorage.getItem(SESSION_KEY_ACTIVE_CONTEXT);
  return raw === '0' || raw === '1' || raw === '2';
}

export function getStoredActiveContext(): ContextType {
  const raw = sessionStorage.getItem(SESSION_KEY_ACTIVE_CONTEXT);
  const parsed = raw ? parseInt(raw, 10) : 0;
  return (isNaN(parsed) ? 0 : parsed) as ContextType;
}

function persistContext(activeOrgId: string | null, activeContext: ContextType) {
  if (activeOrgId) {
    sessionStorage.setItem(SESSION_KEY_ACTIVE_ORG_ID, activeOrgId);
  } else {
    sessionStorage.removeItem(SESSION_KEY_ACTIVE_ORG_ID);
  }
  sessionStorage.setItem(SESSION_KEY_ACTIVE_CONTEXT, String(activeContext));
}

// ── Store ──────────────────────────────────────────────────────

interface OrgContextState {
  /** 当前激活的组织 ID（null = 个人上下文） */
  activeOrgId: string | null;
  /** 上下文类型：0=个人, 1=企业, 2=独立团队 */
  activeContext: ContextType;
  /** 用户所属组织列表（从 /me/contexts 加载） */
  orgs: OrgItem[];
  /** 是否正在切换中 */
  isSwitching: boolean;

  setActiveOrg: (
    orgId: string | null,
    contextType: ContextType,
    options?: { broadcast?: boolean; label?: string }
  ) => void;
  setOrgs: (enterprises: OrgItem[], teams: OrgItem[]) => void;
  patchOrg: (orgId: string, patch: Partial<Omit<OrgItem, 'orgId'>>) => void;
  setIsSwitching: (value: boolean) => void;
  /** 从 sessionStorage 恢复（页面刷新时调用） */
  restoreFromSession: () => void;
  reset: () => void;
}

const initialState = {
  activeOrgId: null,
  activeContext: 0 as ContextType,
  orgs: [],
  isSwitching: false,
};

export const useOrgContextStore = create<OrgContextState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setActiveOrg: (orgId, contextType, options) => {
        persistContext(orgId, contextType);
        const targetOrg = orgId ? get().orgs.find((org) => org.orgId === orgId) : null;
        set({ activeOrgId: orgId, activeContext: contextType });

        if (options?.broadcast) {
          publishOrgContextChangedEvent(
            createOrgContextChangedEvent({
              sourceTabId: getOrgContextTabId(),
              targetOrgId: orgId,
              targetContext: contextType,
              targetLabel:
                options.label ?? targetOrg?.name ?? (orgId ? '组织工作空间' : '个人空间'),
            })
          );
        }
      },

      setOrgs: (enterprises, teams) => {
        set({ orgs: [...enterprises, ...teams] });
      },

      patchOrg: (orgId, patch) => {
        set((state) => ({
          orgs: state.orgs.map((org) => (org.orgId === orgId ? { ...org, ...patch } : org)),
        }));
      },

      setIsSwitching: (value) => set({ isSwitching: value }),

      restoreFromSession: () => {
        const orgId = getStoredActiveOrgId();
        const context = getStoredActiveContext();
        set({ activeOrgId: orgId, activeContext: context });
      },

      reset: () => {
        sessionStorage.removeItem(SESSION_KEY_ACTIVE_ORG_ID);
        sessionStorage.removeItem(SESSION_KEY_ACTIVE_CONTEXT);
        set(initialState);
      },
    }),
    { name: 'OrgContextStore' }
  )
);

/**
 * 从 MyContexts 响应初始化 store 状态
 */
export function syncContextsToStore(
  store: Pick<OrgContextState, 'setActiveOrg' | 'setOrgs'>,
  data: MyContexts
) {
  store.setActiveOrg(data.activeOrgId, data.activeContext, { broadcast: false });
  store.setOrgs(data.enterprises, data.teams);
}
